import React from "react";
import { EmailTemplate } from "@common/components/email-template";
import { IPatientInviteFlow, PatientInviteFlowProps, PatientInviteStep, PatientInviteStepConfig } from "./types";
import { useTemplateController } from "./template";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { I18n } from "react-redux-i18n";
import { PedigreeFolder } from "@common/components/pedigree-folder";
import { IndividualChooser } from "../components/individual-chooser";
import { IndividualListDto } from "@common/dto/individual/list-dto";
import { GetIndividualsRequestParams } from "@common/actions/individuals";
import { ColumnListDto } from "@common/dto/column/list-dto";
import { UpdateDataDto } from "@common/dto/data/update-dto";
import { QuestionnaireType } from "@common/dto/questionnaire/questionnaire-type";
import { IndividualFamily } from "@common/dto/individual/family";
import { isEmail } from "@common/utils/email";
import { HttpErrorDto } from "@common/dto/http";
import { CreatePedigreeAndMoveToFolderParams } from "@common/actions/pedigrees";
import { InvitesGetIndividualsColumnsRequestParams } from "@modules/invite-status/services/invites";

export type IndividualFlowBasicProps = {
  showInfoMessage: (message: string) => void;
  checkPedigreeNameExists: (folderId: FolderPrimaryListDto['id'], pedigreeName: string) => Promise<boolean>;
  createPedigreeAndMoveToFolder: (data: CreatePedigreeAndMoveToFolderParams) => Promise<void>;
  getIndividualFamilyData: (individualId: IndividualListDto['id']) => Promise<IndividualFamily>;
  updateIndividual: (data: UpdateDataDto) => Promise<void>;
  getIndividualsColumns: (params: InvitesGetIndividualsColumnsRequestParams) => Promise<Array<ColumnListDto>>;
  getPedigreeFolders: () => Promise<Array<FolderPrimaryListDto>>;
  getIndividuals: (params: GetIndividualsRequestParams) => Promise<{ data: Array<IndividualListDto>; totalCount: number }>;
};

export type IndividualFlowProps = PatientInviteFlowProps & IndividualFlowBasicProps;

export const useIndividualFlow = (props: IndividualFlowProps): IPatientInviteFlow => {
  const {
    showInfoMessage,
    showErrorMessage,
    getPedigreeFolders,
    onSubmit,
    setCurrentStep,
    questionnaire,
    getIndividuals,
    getIndividualsColumns,
    updateIndividual,
    getIndividualFamilyData,
    createPedigreeAndMoveToFolder,
    checkPedigreeNameExists,
  } = props;
  const {
    emailTemplate,
    emailSubject,
    setEmailSubject,
    setEmailTemplate
  } = useTemplateController();
  const [selectedIndividual, setSelectedIndividual] = React.useState<{ 
    original: IndividualListDto; 
    computed: {
      firstName?: string; 
      lastName?: string; 
      email?: string 
    };
  } | undefined>(undefined);
  const [individualToPedigreeData, setIndividualToPedigreeData] = React.useState<{ name?: string; folderId?: FolderPrimaryListDto['id'] } | undefined>();
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    setEmailSubject(questionnaire?.emailTemplate.subject || '');
    setEmailTemplate(questionnaire?.emailTemplate.body || '');
  }, [questionnaire]);

  const stepsConfig: Partial<Record<PatientInviteStep, PatientInviteStepConfig>> = {
    [PatientInviteStep.individualChooser]: {
      submitBtnProps: {
        disabled: !selectedIndividual
      },
      component: questionnaire && (
        <IndividualChooser
          updateIndividual={updateIndividual}
          questionnaire={questionnaire}
          getIndividualsColumns={getIndividualsColumns}
          showErrorMessage={showErrorMessage}
          getIndividuals={getIndividuals}
          selectedIndividual={selectedIndividual?.original}
          onChange={setSelectedIndividual}
        />
      ),
      onGoToPreviousStep: () => {
        setSelectedIndividual(undefined);
      },
      submitting,
      previousStep: PatientInviteStep.receiverChooser,
      onSubmit: async () => {
        if (!selectedIndividual) {
          return;
        }

        const validations = [
          {
            isValid: () => (
              selectedIndividual.computed.firstName 
              && selectedIndividual.computed.lastName 
              && selectedIndividual.computed.email
            ),
            msg: I18n.t('Please provide first name, last name and email')
          },
          {
            isValid: () => isEmail(selectedIndividual.computed.email),
            msg: I18n.t('Please provide a valid email')
          }
        ];

        const invalidValidation = validations.find(v => !v.isValid());

        if (invalidValidation) {
          showErrorMessage(invalidValidation.msg);

          return;
        }

        try {
          setSubmitting(true);
          const familyData = await getIndividualFamilyData(selectedIndividual.original.id);
  
          if (questionnaire?.questionnaireType === QuestionnaireType.family && !familyData.familyName) {
            setCurrentStep(PatientInviteStep.individualMoveToPedigree);
          } else {
            setCurrentStep(PatientInviteStep.emailTemplate);
          }
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to get individual data'));
        } finally {
          setSubmitting(false);
        }
      }
    },
    [PatientInviteStep.individualMoveToPedigree]: {
      submitBtnProps: {
        disabled: !individualToPedigreeData?.folderId || !individualToPedigreeData.name
      },
      component: (
        <PedigreeFolder 
          getData={getPedigreeFolders} 
          name={individualToPedigreeData?.name} 
          folderId={individualToPedigreeData?.folderId}
          onChange={setIndividualToPedigreeData}
        />
      ),
      onGoToPreviousStep: () => {
        setIndividualToPedigreeData(undefined);
        setSelectedIndividual(undefined);
      },
      submitting,
      previousStep: PatientInviteStep.individualChooser,
      onSubmit: async () => {
        if (!selectedIndividual || !individualToPedigreeData?.name || !individualToPedigreeData?.folderId) {
          return;
        }

        try {
          setSubmitting(true);

          try {
            const exists = await checkPedigreeNameExists(individualToPedigreeData.folderId, individualToPedigreeData.name);

            if (exists) {
              showInfoMessage(I18n.t("The Pedigree Name you entered is already used in this database, please choose another name"));

              return;
            }
          } catch (err) {
            console.error(err);
            showInfoMessage((err as HttpErrorDto | undefined)?.entity || I18n.t("The Pedigree Name you entered is already used in this database, please choose another name"));

            return;
          }

          await createPedigreeAndMoveToFolder({
            pedigreeName: individualToPedigreeData.name,
            pedigreeFolderId: individualToPedigreeData.folderId,
            familyId: selectedIndividual.original.familyId,
          });
          setCurrentStep(PatientInviteStep.emailTemplate);
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to create pedigree'));
        } finally {
          setSubmitting(false);
        }
      }
    },
    [PatientInviteStep.emailTemplate]: {
      component: (
        <EmailTemplate
          subject={emailSubject}
          onSubjectChange={setEmailSubject}
          template={emailTemplate}
          onTemplateChange={setEmailTemplate}
        />
      ),
      submitBtnProps: {
        text: I18n.t('Send an Invite')
      },
      onGoToPreviousStep: () => {
        setSelectedIndividual(undefined);
      },
      previousStep: PatientInviteStep.individualChooser,
      onSubmit: async () => {
        if (!selectedIndividual || !questionnaire) {
          return;
        }

        try {
          setSubmitting(true);
          await onSubmit({
            globalIds: [selectedIndividual.original.id],
            fhq4: questionnaire.fhq4,
            questionnaireId: questionnaire.questionnaireId,
            emailSubject,
            emailBody: emailTemplate,
          });
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to send an invite'));
        } finally {
          setSubmitting(false);
        }
      }
    },
  };

  return {
    stepsConfig,
    getFirstStep: () => PatientInviteStep.individualChooser,
  };
};